import React from 'react';
import { Input } from 'freemium-ui';
import { get } from 'lodash';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  NUMBER_OF_ALLOWED_CUSTOM_HEADERS,
  WorkFlowAuthOptions,
} from 'routes/screens/wflow/constants';
import { WorkflowAuthenticationOptionType } from 'routes/screens/wflow/types';
import { ActionButton } from 'routes/styles';
import Icon from 'shared/components/Icon';
import QuillEditor from 'shared/components/QuillEditor';
import SwitchButton from 'shared/components/SwitchButton';
import { useGetSurvey, useGetTransactionFields } from 'shared/hooks';
import { ErrorField } from 'shared/styles';
import { WorkflowAuthenticationTypes, WorkflowType } from 'shared/types';
import { useGetWorkflowPlaceholders } from '../../hooks';
import { getWorkflowPlaceholders } from '../../utils';
import { ActionContainer } from '../../styles';

type TriggerWebhookProps = {
  disabled: boolean;
  actionIndex: number;
};

const TriggerWebhook = (props: TriggerWebhookProps) => {
  const { disabled, actionIndex } = props;
  const { t } = useTranslation();
  const {
    watch,
    register,
    setValue,
    formState: { errors, submitCount },
  } = useFormContext<WorkflowType>();

  const event = watch('event');
  const survey_id = watch('survey_id');

  const action = watch(`actions.${actionIndex}`);
  const url = watch(`actions.${actionIndex}.action_meta.url`);
  const payload = watch(`actions.${actionIndex}.action_meta.payload`);
  const authentication = watch(
    `actions.${actionIndex}.action_meta.authentication`,
  );
  const custom_headers = watch(
    `actions.${actionIndex}.action_meta.custom_headers`,
  );

  const { data: allPlaceholders } = useGetWorkflowPlaceholders();

  const {
    data: surveyData,
    isFetching: isFetchingSurvey,
    // isError: isSurveyError,
  } = useGetSurvey({
    surveyId: survey_id || '',
    enabled: !!survey_id,
  });

  const { data: transactionFields, isFetching: isFetchingTransactionFields } =
    useGetTransactionFields({
      surveyId: survey_id || '',
      enabled: !!survey_id,
    });

  const onAddCustomHeader = () => {
    if ((custom_headers || []).length >= NUMBER_OF_ALLOWED_CUSTOM_HEADERS)
      return;
    const newCustomHeaders = [...(custom_headers || [])];
    newCustomHeaders.push({ key: '', value: '' });
    setValue(
      `actions.${actionIndex}.action_meta.custom_headers`,
      newCustomHeaders,
    );
  };

  const onDeleteCustomHeader = (indexToDelete) => {
    const newCustomHeaders = [...(custom_headers || [])].filter(
      (ch, idx) => idx !== indexToDelete,
    );
    setValue(
      `actions.${actionIndex}.action_meta.custom_headers`,
      newCustomHeaders,
    );
  };

  const placeholders = React.useMemo(
    () =>
      getWorkflowPlaceholders({
        surveyData,
        transactionFields,
        allPlaceholders: {
          ...allPlaceholders,
        },
        event,
      }),
    [surveyData, allPlaceholders, event],
  );

  const placeholdersForUrl = React.useMemo(() => {
    return placeholders.map((placeholder) => {
      if (placeholder.label.toLowerCase() === 'response') {
        return {
          ...placeholder,
          options: placeholder.options.filter(
            (option) => !option.id.includes('question'),
          ),
        };
      }
      return placeholder;
    });
  }, [placeholders]);

  const workFlowActionOptions = React.useMemo<
    WorkflowAuthenticationOptionType[]
  >(
    () =>
      Object.keys(WorkFlowAuthOptions).map((key) => ({
        id: key as WorkflowAuthenticationTypes,
        label: t(WorkFlowAuthOptions[key]),
      })),
    [event],
  );

  return (
    <ActionContainer>
      <div className="t-w-h-row">
        <div className="t-w-h-flex">
          <QuillEditor
            id="quill-wf-url"
            getValueAs="HTML"
            placeholders={placeholdersForUrl}
            placeholderConfig={{ isAddSpaceAtEnd: false }}
            label={`${t('forms.placeholders.webhook_url')}`}
            disabled={isFetchingSurvey || disabled}
            value={url || ''}
            onChange={(val: string) =>
              setValue(`actions.${actionIndex}.action_meta.url`, val, {
                shouldValidate: true,
              })
            }
            isErrored={
              submitCount > 0 &&
              !!get(errors, `actions.${actionIndex}.action_meta.url.message`)
            }
            errorText={get(
              errors,
              `actions.${actionIndex}.action_meta.url.message`,
              '',
            )}
          />
        </div>
        <div>
          <div className="wf-sb-label">
            {t('workflow.action.webhook_auth.title') || ''}
          </div>
          <div className="wf-sb-toggle">
            <SwitchButton
              {...register(`actions.${actionIndex}.action_meta.url`)}
              value={authentication || ''}
              disabled={disabled}
              onChange={(item: WorkflowAuthenticationOptionType) => {
                if (item.id === authentication) return;
                setValue(
                  `actions.${actionIndex}.action_meta.authentication`,
                  item.id,
                );
                if (item.id === 'None') {
                  setValue(
                    `actions.${actionIndex}.action_meta.basic_auth`,
                    null,
                  );
                  setValue(`actions.${actionIndex}.action_meta.api_key`, null);
                }
                if (item.id === 'api_key') {
                  setValue(
                    `actions.${actionIndex}.action_meta.basic_auth`,
                    null,
                  );
                }
                if (item.id === 'basic_auth') {
                  setValue(`actions.${actionIndex}.action_meta.api_key`, null);
                }
              }}
              items={workFlowActionOptions}
              itemLabelIdentifier="label"
              itemValueIdentifier="id"
            />
          </div>
        </div>
      </div>
      {action?.action_meta?.authentication === 'basic_auth' && (
        <div className="t-w-h-row">
          <div className="t-w-h-flex">
            <Input
              {...register(
                `actions.${actionIndex}.action_meta.basic_auth.user`,
              )}
              id="user"
              label={`${t('user_name')}`}
              disabled={disabled}
              isRequired
              isErrored={
                submitCount > 0 &&
                !!get(
                  errors,
                  `actions.${actionIndex}.action_meta.basic_auth.user.message`,
                )
              }
              errorText={get(
                errors,
                `actions.${actionIndex}.action_meta.basic_auth.user.message`,
                '',
              )}
            />
          </div>
          <div className="t-w-h-flex">
            <Input
              {...register(
                `actions.${actionIndex}.action_meta.basic_auth.pass`,
              )}
              id="pass"
              label={`${t('password')}`}
              disabled={disabled}
              isRequired
              isErrored={
                submitCount > 0 &&
                !!get(
                  errors,
                  `actions.${actionIndex}.action_meta.basic_auth.pass.message`,
                )
              }
              errorText={get(
                errors,
                `actions.${actionIndex}.action_meta.basic_auth.pass.message`,
                '',
              )}
            />
          </div>
        </div>
      )}
      {action?.action_meta?.authentication === 'api_key' && (
        <div className="t-w-h-row">
          <div className="t-w-h-flex">
            <Input
              {...register(`actions.${actionIndex}.action_meta.api_key.key`)}
              id="key"
              label={`${t('forms.labels.key')}`}
              disabled={disabled}
              isRequired
              isErrored={
                submitCount > 0 &&
                !!get(
                  errors,
                  `actions.${actionIndex}.action_meta.api_key.key.message`,
                )
              }
              errorText={get(
                errors,
                `actions.${actionIndex}.action_meta.api_key.key.message`,
                '',
              )}
            />
          </div>
          <div className="t-w-h-flex">
            <Input
              {...register(`actions.${actionIndex}.action_meta.api_key.value`)}
              id="value"
              label={`${t('forms.labels.value')}`}
              disabled={disabled}
              isRequired
              isErrored={
                submitCount > 0 &&
                !!get(
                  errors,
                  `actions.${actionIndex}.action_meta.api_key.value.message`,
                )
              }
              errorText={get(
                errors,
                `actions.${actionIndex}.action_meta.api_key.value.message`,
                '',
              )}
            />
          </div>
        </div>
      )}
      {(custom_headers || []).length > 0 && (
        <>
          <div className="wf-sb-label">
            {t('workflow.action.webhook_auth.ch')}
          </div>
          {custom_headers?.map((ch, idx) => (
            <div className="t-w-h-row" key={`${event}-${idx}`}>
              <div className="t-w-h-flex">
                <Input
                  {...register(
                    `actions.${actionIndex}.action_meta.custom_headers.${idx}.key`,
                  )}
                  id="key"
                  disabled={disabled}
                  placeholder={`${t('forms.placeholders.enter_key')}`}
                  isRequired
                  isErrored={
                    submitCount > 0 &&
                    !!get(
                      errors,
                      `actions.${actionIndex}.action_meta.custom_headers.${idx}.key.message`,
                    )
                  }
                  errorText={get(
                    errors,
                    `actions.${actionIndex}.action_meta.custom_headers.${idx}.key.message`,
                    '',
                  )}
                />
              </div>
              <div className="t-w-h-flex">
                <Input
                  {...register(
                    `actions.${actionIndex}.action_meta.custom_headers.${idx}.value`,
                  )}
                  id="value"
                  disabled={disabled}
                  placeholder={`${t('forms.placeholders.enter_value')}`}
                  isRequired
                  isErrored={
                    submitCount > 0 &&
                    !!get(
                      errors,
                      `actions.${actionIndex}.action_meta.custom_headers.${idx}.value.message`,
                    )
                  }
                  errorText={get(
                    errors,
                    `actions.${actionIndex}.action_meta.custom_headers.${idx}.value.message`,
                    '',
                  )}
                />
              </div>
              <ActionButton
                onClick={() => onDeleteCustomHeader(idx)}
                className={disabled ? 'disabled' : ''}
              >
                <div>
                  <Icon width="12" height="12" glyph="minus" />
                </div>
              </ActionButton>
            </div>
          ))}
          {!!get(
            errors,
            `actions.${actionIndex}.action_meta.custom_headers.message`,
          ) && (
            <div className="t-w-h-row">
              <ErrorField>
                {get(
                  errors,
                  `actions.${actionIndex}.action_meta.custom_headers.message`,
                  '',
                )}
              </ErrorField>
            </div>
          )}
        </>
      )}
      <ActionButton
        onClick={onAddCustomHeader}
        className={
          disabled ||
          (custom_headers &&
            custom_headers?.length >= NUMBER_OF_ALLOWED_CUSTOM_HEADERS)
            ? 'disabled'
            : ''
        }
      >
        <div>
          <Icon width="12" height="12" glyph="plus" />
        </div>
        <div>{`${t('add')} ${t(
          'workflow.action.webhook_auth.ch',
        ).toLowerCase()}`}</div>
      </ActionButton>
      <div className="t-w-h-row" />
      <QuillEditor
        id="quill-wf-payload"
        getValueAs="HTML"
        placeholders={placeholders}
        placeholderConfig={{ isAddSpaceAtEnd: false }}
        label={`${t('forms.labels.content')}`}
        disabled={isFetchingSurvey || disabled}
        value={payload || ''}
        onChange={(val: string) =>
          setValue(`actions.${actionIndex}.action_meta.payload`, val, {
            shouldValidate: true,
          })
        }
        isErrored={
          submitCount > 0 &&
          !!get(errors, `actions.${actionIndex}.action_meta.payload.message`)
        }
        errorText={get(
          errors,
          `actions.${actionIndex}.action_meta.payload.message`,
          '',
        )}
      />
    </ActionContainer>
  );
};

export default TriggerWebhook;
